import { DefaultQueryParams } from '@frontend/api-utils';
import { ChartClient } from '@frontend/chart/api';
import {
    PudoTransactionVolumeByReceiverListChart,
    PudoTransactionVolumeBySenderListChart,
    takeRankedChartSeries,
    toChartListItems
} from '@frontend/chart/components';
import { Chart, ChartQueryParam } from '@frontend/chart/types';
import { useEffect, useState } from 'react';

import { getLast30DaysChartRange } from '../../../statistics/pudo/last-30-days-chart-range';
import { useInsightDetailMode } from '../insight-detail-mode.context';
import { useInsightFetchRange } from '../insight-detail/use-insight-fetch-range';

export type PudoTransactionUserGroup = 'sender' | 'receiver';

/** Ranked preview size on the insights overview cards. */
const OVERVIEW_USER_LIMIT = 8;

export interface PudoTransactionVolumeByUserItem {
    label: string;
    transactionCount: number;
}

const isDisplayableLabel = (value: string): boolean => {
    const normalized = value.trim().toLowerCase();
    return normalized.length > 0 && normalized !== 'none' && normalized !== 'default';
};

const toUserItems = (chart: Chart): PudoTransactionVolumeByUserItem[] =>
    toChartListItems(chart, '')
        .filter((item) => isDisplayableLabel(item.name))
        .map((item) => ({
            label: item.name,
            transactionCount: item.value
        }));

export const usePudoTransactionVolumeByUserList = (accountId: string, userGroup: PudoTransactionUserGroup) => {
    const isDetailMode = useInsightDetailMode();
    const [items, setItems] = useState<PudoTransactionVolumeByUserItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchRange = useInsightFetchRange(getLast30DaysChartRange);

    useEffect(() => {
        if (!accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const { after, before } = fetchRange;

        const definition = userGroup === 'sender' ? PudoTransactionVolumeBySenderListChart.definition : PudoTransactionVolumeByReceiverListChart.definition;

        ChartClient.fetchChartData(accountId, definition, {
            [DefaultQueryParams.AFTER]: after,
            [DefaultQueryParams.BEFORE]: before,
            ...fetchRange.entityParams,
            [DefaultQueryParams.SORTDIRECTION]: 'desc',
            [ChartQueryParam.GROUP_BY]: userGroup
        })
            .then((chart) => {
                if (cancelled) return;
                // Overview: top N. Detail: full ranked series (paginated in the UI).
                const ranked = isDetailMode
                    ? takeRankedChartSeries(chart, 'highest', Number.POSITIVE_INFINITY)
                    : takeRankedChartSeries(chart, 'highest', OVERVIEW_USER_LIMIT);
                setItems(toUserItems(ranked));
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [accountId, userGroup, fetchRange.key, isDetailMode]);

    return { items, isLoading, hasData: items.length > 0, isDetailMode };
};
